import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  House,
  History,
  ClipboardCheck,
  FileText,
  Users,
  Building2,
  X,
  UserPen,
  Landmark,
  MapPinned,
  Database,
} from "lucide-react";

import { useUnsavedChanges } from "../../../pages/admin/UnsavedChangesProvider";
import "./AdminPlatformSidebar.css";

export default function AdminPlatformSidebar({
  title,
  mobileOpen = false,
  onClose,
}) {
  const { t } = useTranslation("admin-sidebar");
  const location = useLocation();
  const { requestNavigation } = useUnsavedChanges();

  const goTo = React.useCallback(
    (path) => {
      requestNavigation(path);
      onClose?.();
    },
    [requestNavigation, onClose],
  );

  const items = React.useMemo(
    () => [
      {
        key: "dashboard",
        label: t("items.dashboard"),
        icon: House,
        path: "/admin/dashboard",
        isActive:
          location.pathname === "/admin" ||
          location.pathname === "/admin/dashboard",
      },
      {
        key: "activity",
        label: t("items.activity"),
        icon: History,
        path: "/admin/activity",
        isActive: location.pathname.startsWith("/admin/activity"),
      },
      {
        key: "requests",
        label: t("items.requests"),
        icon: ClipboardCheck,
        path: "/admin/requests",
        isActive: location.pathname.startsWith("/admin/requests"),
      },
      {
        key: "reporting",
        label: t("items.reporting"),
        icon: FileText,
        path: "/admin/reporting",
        isActive: location.pathname.startsWith("/admin/reporting"),
      },
      {
        key: "users",
        label: t("items.users"),
        icon: Users,
        path: "/admin/users",
        isActive: location.pathname.startsWith("/admin/users"),
      },
      {
        key: "organisations",
        label: t("items.organisations"),
        icon: Building2,
        path: "/admin/organisations",
        isActive: location.pathname.startsWith("/admin/organisations"),
      },
      {
        key: "market-regions",
        label: t("navigation.marketRegions", "Market Regions"),
        icon: MapPinned,
        path: "/admin/market-regions",
      },
      {
        key: "reference-data",
        label: t("items.referenceData", "Reference Data"),
        icon: Database,
        path: "/admin/reference-data",
        isActive: location.pathname.startsWith("/admin/reference-data"),
      },
      {
        key: "fundingSources",
        label: t("items.fundingSources"),
        icon: Landmark,
        path: "/admin/funding-sources",
        isActive: location.pathname.startsWith("/admin/funding-sources"),
      },
      {
        key: "account",
        label: t("items.account"),
        icon: UserPen,
        path: "/admin/account",
        isActive: location.pathname.startsWith("/admin/account"),
      },
    ],
    [t, location.pathname],
  );

  const resolvedTitle = title || t("title");

  return (
    <>
      {/* DESKTOP SIDEBAR */}
      <aside className="platform-sidebar">
        <div className="platform-sidebar__panel">
          <div className="platform-sidebar__title">{resolvedTitle}</div>

          <nav
            className="platform-sidebar__nav"
            aria-label={t("aria.desktopNav")}
          >
            {items.map((item) => {
              const Icon = item.icon;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => goTo(item.path)}
                  className={`platform-sidebar__item ${
                    item.isActive ? "is-active" : ""
                  }`}
                  aria-current={item.isActive ? "page" : undefined}
                >
                  <span className="platform-sidebar__icon-wrap">
                    <Icon size={26} strokeWidth={2.1} />
                  </span>

                  <span className="platform-sidebar__label">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* MOBILE OVERLAY */}
      <div
        className={`platform-sidebar__mobile-overlay ${
          mobileOpen ? "is-open" : ""
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* MOBILE SIDEBAR */}
      <aside
        className={`platform-sidebar__mobile ${mobileOpen ? "is-open" : ""}`}
      >
        <div className="platform-sidebar__mobile-header">
          <span className="platform-sidebar__mobile-title">
            {resolvedTitle}
          </span>

          <button
            type="button"
            className="platform-sidebar__mobile-close"
            onClick={onClose}
            aria-label={t("aria.closeMenu")}
          >
            <X size={22} />
          </button>
        </div>

        <nav
          className="platform-sidebar__mobile-nav"
          aria-label={t("aria.mobileNav")}
        >
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => goTo(item.path)}
                className={`platform-sidebar__mobile-item ${
                  item.isActive ? "is-active" : ""
                }`}
                aria-current={item.isActive ? "page" : undefined}
              >
                <span className="platform-sidebar__icon-wrap">
                  <Icon size={24} strokeWidth={2.1} />
                </span>

                <span className="platform-sidebar__mobile-label">
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
